/**
 * Complementary products add-to-cart (PDP "Complementary products" block).
 *
 * The theme's shared <form is="product-form"> element redirects to /cart after
 * a successful add when the cart type is "page" (see ProductForm in theme.js).
 * For the complementary-products block the customer should stay on the PDP.
 * Two paths (both marked in snippets/horizontal-product.liquid via the
 * stay_on_page parameter):
 *
 * 1. Single-variant products render an inline form WITHOUT is="product-form",
 *    marked with data-complementary-atc. Handled by the bubble-phase listener.
 * 2. Products with multiple variants or selling plans open a quick-buy drawer
 *    (marked with data-complementary-atc-drawer) whose inner form comes from
 *    the product page and still uses is="product-form". The capture-phase
 *    listener intercepts its submit before ProductForm's own handler runs,
 *    so the redirect never happens; the drawer is closed on success instead.
 *
 * Both paths perform the same AJAX add and dispatch the same cart events as
 * ProductForm, minus the redirect. Delegated listeners are used because the
 * block's content is injected after page load by <product-recommendations>.
 */
(function () {
  // Path 1: inline single-variant forms
  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-complementary-atc')) {
      return;
    }

    event.preventDefault();
    addToCart(form).then((succeeded) => {
      if (succeeded) {
        showAddedFeedback(form);
      }
    });
  });

  // Path 2: quick-buy drawer forms (selling plans / multiple variants).
  // Capture phase so it runs before the is="product-form" submit listener
  // attached to the form itself; stopPropagation keeps that listener (and its
  // /cart redirect) from ever running.
  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const drawer = form.closest('quick-buy-drawer[data-complementary-atc-drawer]');

    if (!drawer) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    addToCart(form).then((succeeded) => {
      if (succeeded) {
        drawer.hide();
      }
    });
  }, true);

  async function addToCart(form) {
    const submitButton = form.querySelector('button[type="submit"]');
    submitButton?.setAttribute('disabled', 'disabled');
    submitButton?.setAttribute('aria-busy', 'true'); // Shows the custom-button loader

    // Same bundled-sections handshake as ProductForm: the (hidden) cart drawer
    // expects cart:change events to carry its re-rendered section HTML.
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const formData = new FormData(form);
    formData.set('sections', sectionsToBundle.join(','));

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const responseJson = await response.json();

      if (!response.ok) {
        console.error('[complementary-atc]', responseJson['description'] || response.statusText);
        return false;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent['sections'] = responseJson['sections'];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        })
      );

      // The theme's CartCount setter animates non-zero -> non-zero changes and
      // on this store the vendor animation's finished promise wedges, freezing
      // the badge at the old number (pre-existing quirk; the page-cart flow
      // never exercised it because it always redirected before a second
      // in-page update). Reconcile the badge once the animation should be done.
      setTimeout(() => {
        document.querySelectorAll('cart-count').forEach((badge) => {
          if (parseInt(badge.innerText, 10) !== cartContent['item_count']) {
            badge.shadowRoot?.firstElementChild?.getAnimations().forEach((animation) => animation.cancel());
            badge.innerText = cartContent['item_count'];
          }
        });
      }, 1200);

      return true;
    } catch (error) {
      console.error('[complementary-atc]', error);
      return false;
    } finally {
      submitButton?.removeAttribute('disabled');
      submitButton?.removeAttribute('aria-busy');
    }
  }

  function showAddedFeedback(form) {
    const submitButton = form.querySelector('button[type="submit"]');

    if (!submitButton) {
      return;
    }

    // is="custom-button" moves the label into a child div (same convention as BuyButtons in theme.js)
    const labelElement = submitButton.getAttribute('is') === 'custom-button' ? submitButton.firstElementChild : submitButton;
    const originalLabel = labelElement.textContent;

    labelElement.textContent = form.getAttribute('data-added-text') || originalLabel;

    setTimeout(() => {
      labelElement.textContent = originalLabel;
    }, 2000);
  }
})();
